<template>
  <div class="bg-white dark:bg-gray-900 rounded-lg shadow-lg p-3 hover:shadow-xl transition-shadow duration-300">
    <!-- Header with Download Icon -->
    <div class="flex items-center space-x-2 mb-3">
      <div class="p-1.5 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg">
        <svg class="w-4 h-4 text-white animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"/>
        </svg>
      </div>
      <h4 class="text-xs font-semibold text-gray-900 dark:text-white">Downloads</h4>
      <div class="flex-1"></div>
      <!-- Total Downloads Badge -->
      <div class="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-xs rounded-full font-medium animate-pulse">
        {{ resolutions.length }}
      </div>
    </div>
    
    <!-- Download Options with Animations -->
    <div class="space-y-1">
      <button
        v-for="(resolution, index) in resolutions"
        :key="resolution.dimensions"
        @click="downloadResolution(resolution)"
        :disabled="isDownloading"
        class="w-full group relative overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 hover:border-green-400 dark:hover:border-green-500 transition-all duration-300 hover:shadow-md hover:scale-102 active:scale-98"
        :style="{ animationDelay: `${index * 50}ms` }"
        :class="{ 'animate-pulse opacity-50': isDownloading }"
      >
        <!-- Background Gradient on Hover -->
        <div class="absolute inset-0 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/10 dark:to-emerald-900/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        
        <!-- Content -->
        <div class="relative flex items-center justify-between p-2">
          <div class="flex items-center space-x-2">
            <!-- Quality Icon -->
            <div class="flex-shrink-0">
              <div :class="[
                'p-1.5 rounded-md transition-all duration-300',
                getQualityClass(resolution.name)
              ]">
                <!-- HD/4K Icons -->
                <svg v-if="resolution.name.includes('4K') || resolution.name.includes('2160')" 
                     class="w-3 h-3 transform group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm2 2v8h2V8h2v6h2V6H7V6z" clip-rule="evenodd"/>
                </svg>
                <svg v-else-if="resolution.name.includes('HD') || resolution.name.includes('1080')" 
                     class="w-3 h-3 transform group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd"/>
                </svg>
                <!-- Default Download Icon -->
                <svg v-else class="w-3 h-3 transform group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"/>
                </svg>
              </div>
            </div>
            
            <!-- Resolution Info -->
            <div class="flex-1 text-left">
              <div class="text-xs font-medium text-gray-900 dark:text-white group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors">
                {{ resolution.name }}
              </div>
              <div class="text-xs text-gray-500 dark:text-gray-400 flex items-center space-x-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
                <span>{{ resolution.dimensions }}</span>
              </div>
            </div>
          </div>
          
          <!-- File Size and Download Icon -->
          <div class="flex items-center space-x-2">
            <div class="text-xs text-gray-500 dark:text-gray-400 font-medium">
              {{ resolution.size }}
            </div>
            
            <!-- Download Arrow with Animation -->
            <div class="flex-shrink-0">
              <svg v-if="!isDownloading" 
                   class="w-4 h-4 text-gray-400 group-hover:text-green-500 transform group-hover:translate-y-0.5 group-hover:scale-110 transition-all duration-300" 
                   fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"/>
              </svg>
              
              <!-- Loading Spinner -->
              <svg v-else class="w-4 h-4 text-green-500 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          </div>
        </div>
        
        <!-- Shimmer Effect on Hover -->
        <div class="absolute inset-0 -translate-x-full group-hover:translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 ease-out"></div>
        
        <!-- Progress Bar for Downloads -->
        <div v-if="isDownloading" class="absolute bottom-0 left-0 h-0.5 bg-green-500 animate-pulse" style="width: 60%"></div>
      </button>
    </div>
    
    <!-- Download Stats -->
    <div class="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
      <div class="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <div class="flex items-center space-x-1">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
          </svg>
          <span>Fast download</span>
        </div>
        <div class="flex items-center space-x-1">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <span>High quality</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';

interface Resolution {
  name: string
  dimensions: string
  size: string
  url: string
}

interface Props {
  resolutions: Resolution[]
  isDownloading: boolean
}

defineProps<Props>()

const emit = defineEmits<{
  (e: 'download', resolution: Resolution): void;
  (e: 'create-account'): void;
  (e: 'download-success'): void;
  (e: 'download-error', error: string): void;
}>();

const isDownloading = ref(false);
// Handle successful download
const handleDownloadSuccess = () => {
  // Emit the download success event to the parent
  emit('download-success');
};

const getQualityClass = (name: string): string => {
  if (name.includes('4K') || name.includes('2160')) {
    return 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
  } else if (name.includes('HD') || name.includes('1080')) {
    return 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
  } else if (name.includes('Original')) {
    return 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
  }
  return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
}

const downloadResolution = async (resolution: Resolution) => {
  if (isDownloading.value) return;
  
  isDownloading.value = true;
  try {
    emit('download', resolution);
    // The actual download will be handled by the parent component
    // which will emit a 'download-success' event when done
  } catch (error) {
    console.error('Download failed:', error);
  } finally {
    isDownloading.value = false;
  }
};

// Expose methods to parent component
defineExpose({
  isDownloading,
  handleDownloadSuccess
});
</script>

<style scoped>
.hover\:scale-102:hover {
  transform: scale(1.02);
}

.active\:scale-98:active {
  transform: scale(0.98);
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.animate-slide-in {
  animation: slideIn 0.3s ease-out forwards;
}
</style>